<template>
    <div class="p-4">
      <h2 class="text-xl font-bold mb-4">Симулятор столкновения тел</h2>
  
      <div class="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label>Масса тела 1 (кг)</label>
          <input v-model.number="m1" type="number" class="input" />
  
          <label>Скорость тела 1 (м/с)</label>
          <input v-model.number="v1" type="number" class="input" />
  
          <label>Направление тела 1</label>
          <select v-model="dir1" class="input">
            <option value="right">вправо</option>
            <option value="left">влево</option>
          </select>
        </div>
  
        <div>
          <label>Масса тела 2 (кг)</label>
          <input v-model.number="m2" type="number" class="input" />
  
          <label>Скорость тела 2 (м/с)</label>
          <input v-model.number="v2" type="number" class="input" />
  
          <label>Направление тела 2</label>
          <select v-model="dir2" class="input">
            <option value="right">вправо</option>
            <option value="left">влево</option>
          </select>
        </div>
      </div>
  
      <label class="flex items-center mb-4">
        <input type="checkbox" v-model="elastic" class="mr-2" />
        Упругое столкновение
      </label>
  
      <button @click="startSimulation" class="btn mb-4">Запустить симуляцию</button>
  
      <canvas ref="canvas" :width="600" :height="150" class="border mb-4"></canvas>
  
      <div v-if="resultShown">
        <p><strong>Результаты после столкновения:</strong></p>
        <p>Скорость тела 1: {{ v1f.toFixed(2) }} м/с</p>
        <p>Скорость тела 2: {{ v2f.toFixed(2) }} м/с</p>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from 'vue';
  
  const canvas = ref(null);
  
  const m1 = ref(2);
  const m2 = ref(2);
  const v1 = ref(2);
  const v2 = ref(1);
  const dir1 = ref('right');
  const dir2 = ref('left');
  const elastic = ref(true);
  
  const v1f = ref(0);
  const v2f = ref(0);
  const resultShown = ref(false);
  
  let ctx;
  let ball1 = reactive({ x: 100, y: 75 });
  let ball2 = reactive({ x: 500, y: 75 });
  
  let collided = false;
  let pulse = 1;
  
  function getDirectionalSpeed(speed, dir) {
    return dir === 'left' ? -speed : speed;
  }
  
  function animateCollisionPulse() {
    if (!collided) return;
  
    let frames = 10;
    let direction = -1;
  
    const pulseAnim = () => {
      pulse += direction * 0.05;
  
      if (pulse <= 0.9 || pulse >= 1.1) direction *= -1;
      if (frames-- <= 0) {
        pulse = 1;
        collided = false;
        return;
      }
  
      drawBalls(pulse);
      requestAnimationFrame(pulseAnim);
    };
  
    pulseAnim();
  }
  
  function drawBalls(scale = 1) {
    ctx.clearRect(0, 0, 600, 150);
  
    // Шар 1
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 10;
    ctx.fillStyle = 'red';
    ctx.beginPath();
    ctx.arc(ball1.x, ball1.y, 20 * scale, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
  
    // Шар 2
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 10;
    ctx.fillStyle = 'blue';
    ctx.beginPath();
    ctx.arc(ball2.x, ball2.y, 20 * scale, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
  }
  
  function calculateVelocities() {
    const m1v = m1.value;
    const m2v = m2.value;
    const u1 = getDirectionalSpeed(v1.value, dir1.value);
    const u2 = getDirectionalSpeed(v2.value, dir2.value);
  
    if (elastic.value) {
      v1f.value = ((m1v - m2v) * u1 + 2 * m2v * u2) / (m1v + m2v);
      v2f.value = ((m2v - m1v) * u2 + 2 * m1v * u1) / (m1v + m2v);
    } else {
      const vf = (m1v * u1 + m2v * u2) / (m1v + m2v);
      v1f.value = vf;
      v2f.value = vf;
    }
  }
  
  function startSimulation() {
    calculateVelocities();
    resultShown.value = false;
  
    let x1 = 100;
    let x2 = 500;
    const dt = 0.016;
    const simDuration = 3;
    const steps = simDuration / dt;
    let step = 0;
  
    const vx1 = getDirectionalSpeed(v1.value, dir1.value) * 50;
    const vx2 = getDirectionalSpeed(v2.value, dir2.value) * 50;
  
    const anim = () => {
      if (step >= steps) {
        resultShown.value = true;
        ball1.x = x1;
        ball2.x = x2;
        drawBalls();
        return;
      }
  
      if (Math.abs(x1 - x2) > 40) {
        x1 += vx1 * dt;
        x2 += vx2 * dt;
      } else {
        if (!collided) {
          const newVx1 = v1f.value * 50;
          const newVx2 = v2f.value * 50;
          x1 += newVx1 * dt;
          x2 += newVx2 * dt;
          collided = true;
          animateCollisionPulse();
        } else {
          const newVx1 = v1f.value * 50;
          const newVx2 = v2f.value * 50;
          x1 += newVx1 * dt;
          x2 += newVx2 * dt;
        }
      }
  
      ball1.x = x1;
      ball2.x = x2;
  
      drawBalls();
      step++;
      requestAnimationFrame(anim);
    };
  
    anim();
  }
  
  onMounted(() => {
    ctx = canvas.value.getContext('2d');
    drawBalls();
  });
  </script>
  
  <style scoped>
  .input {
    display: block;
    margin-bottom: 8px;
    padding: 4px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  .btn {
    padding: 6px 12px;
    background-color: #2563eb;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  </style>
  